const GUEST_KEY = 'netprobuilder-canvas-camera';
const userKey = (userId: string) => `${GUEST_KEY}:${userId}`;

export const CANVAS_ZOOM_LEVELS = [0.50, 0.60, 0.70, 0.80, 0.90, 1, 1.10, 1.20, 1.30, 1.40, 1.50] as const;

/** Canvas zoom at which equipment ports use the layout-editor detail tiles. */
export const PORT_DETAIL_ZOOM = 1.30;

/** At 50–100% zoom, ports are colour/outline only (no labels or type icons). */
export function portChromeVisibleAtZoom(zoom: number): boolean {
  return zoom > 1;
}

export interface CanvasCameraView {
  panX: number;
  panY: number;
  zoom: number;
}

export interface CanvasCameraSession {
  activeDesignId: string;
  openDesigns: string[];
}

interface CanvasCameraStore {
  v: 1;
  cameras: Record<string, CanvasCameraView>;
  sessions: Record<string, CanvasCameraSession>;
}

function storageKey(userId: string | null | undefined): string {
  return userId ? userKey(userId) : GUEST_KEY;
}

function viewKey(projectId: string, designId: string): string {
  return `${projectId}::${designId}`;
}

export function clampCanvasZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  return CANVAS_ZOOM_LEVELS.reduce((prev, curr) =>
    Math.abs(curr - zoom) < Math.abs(prev - zoom) ? curr : prev
  );
}

function emptyStore(): CanvasCameraStore {
  return { v: 1, cameras: {}, sessions: {} };
}

function readStore(userId: string | null | undefined): CanvasCameraStore {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as Partial<CanvasCameraStore>;
    if (!parsed || parsed.v !== 1 || typeof parsed !== 'object') return emptyStore();
    return {
      v: 1,
      cameras: parsed.cameras && typeof parsed.cameras === 'object' ? parsed.cameras : {},
      sessions: parsed.sessions && typeof parsed.sessions === 'object' ? parsed.sessions : {},
    };
  } catch {
    return emptyStore();
  }
}

function writeStore(userId: string | null | undefined, store: CanvasCameraStore): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(store));
  } catch {
    // Ignore quota / private-mode failures
  }
}

function parseView(raw: unknown): CanvasCameraView | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const panX = typeof o.panX === 'number' && Number.isFinite(o.panX) ? o.panX : null;
  const panY = typeof o.panY === 'number' && Number.isFinite(o.panY) ? o.panY : null;
  const zoom = typeof o.zoom === 'number' && Number.isFinite(o.zoom) ? clampCanvasZoom(o.zoom) : null;
  if (panX === null || panY === null || zoom === null) return null;
  return { panX, panY, zoom };
}

export function readCanvasCamera(
  userId: string | null | undefined,
  projectId: string,
  designId: string
): CanvasCameraView | null {
  return parseView(readStore(userId).cameras[viewKey(projectId, designId)]);
}

export function writeCanvasCamera(
  userId: string | null | undefined,
  projectId: string,
  designId: string,
  view: CanvasCameraView
): void {
  const store = readStore(userId);
  store.cameras[viewKey(projectId, designId)] = {
    panX: view.panX,
    panY: view.panY,
    zoom: clampCanvasZoom(view.zoom),
  };
  writeStore(userId, store);
}

export function readCanvasCameraSession(
  userId: string | null | undefined,
  projectId: string
): CanvasCameraSession | null {
  const session = readStore(userId).sessions[projectId];
  if (!session || typeof session !== 'object') return null;
  if (typeof session.activeDesignId !== 'string' || !Array.isArray(session.openDesigns)) return null;
  return {
    activeDesignId: session.activeDesignId,
    openDesigns: session.openDesigns.filter((id): id is string => typeof id === 'string'),
  };
}

export function writeCanvasCameraSession(
  userId: string | null | undefined,
  projectId: string,
  session: CanvasCameraSession
): void {
  const store = readStore(userId);
  store.sessions[projectId] = {
    activeDesignId: session.activeDesignId,
    openDesigns: session.openDesigns,
  };
  writeStore(userId, store);
}
